import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Platform, Text, View } from 'react-native';
import { auth, db, onAuthStateChanged, resilientOnSnapshot } from '../firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';

const NavigationContext = createContext();

// Global flags to prevent duplicate listener creation across hot-reloads
if (typeof window !== 'undefined') {
  window.__firestoreListeners = window.__firestoreListeners || {
    userListener: null,
    maintenanceListener: null,
    authListener: null,
  };
}

// Screen to URL mapping
const screenToUrl = {
  'MainPage': '/coming-soon',
  'Home': '/home',
  'Login': '/login',
  'Register': '/register',
  'Onboarding': '/onboarding',
  'Dashboard': '/dashboard',
  'Explore': '/explore',
  'SameGameParlay': '/optimizer',
  'Community': '/community',
  'SgpFlow': '/results',
  'Profile': '/profile',
  'ForgotPassword': '/forgotpassword',
  'ResetPassword': '/reset-password',
  'Admin': '/admin',
  'Plans': '/plans',
  'Success': '/subscription-success',
  'Suspended': '/suspended',
  'Docs': '/docs',
  'Privacy': '/privacy',
  'Terms': '/terms',
};

// URL to screen mapping
const urlToScreen = {
  '/': 'Home',
  '/home': 'Home',
  '/login': 'Login',
  '/register': 'Register',
  '/onboarding': 'Onboarding',
  '/dashboard': 'Dashboard',
  '/explore': 'Explore',
  '/optimizer': 'SameGameParlay',
  '/community': 'Community',
  '/results': 'SgpFlow',
  '/optimizer/results': 'SgpFlow',
  '/sgp': 'SameGameParlay', // Redirect old URL
  '/sgpflow': 'SgpFlow', // Redirect old URL
  '/optimizer/flow': 'SgpFlow', // Redirect old URL
  '/profile': 'Profile',
  '/forgotpassword': 'ForgotPassword',
  '/reset-password': 'ResetPassword',
  '/coming-soon': 'MainPage',
  '/admin': 'Admin',
  '/admin-login': 'AdminLogin',
  '/plans': 'Plans',
  '/subscription-success': 'Success',
  '/suspended': 'Suspended',
  '/docs': 'Docs',
  '/privacy': 'Privacy',
  '/terms': 'Terms',
};

// Helper function to build URL with query parameters
const buildUrlWithParams = (screenName, payload, fromScreen) => {
  let baseUrl = screenToUrl[screenName] || '/';
  
  // For SgpFlow, use different URL based on where we're coming from
  if (screenName === 'SgpFlow') {
    if (fromScreen === 'SameGameParlay') {
      baseUrl = '/optimizer/results';
    } else {
      baseUrl = '/results';
    }
    
    // Add query parameter if query exists
    if (payload?.query) {
      const params = new URLSearchParams();
      params.set('q', payload.query);
      const fixtureId = payload.fixtureId || payload.fixture_id;
      if (fixtureId) params.set('fixture', String(fixtureId));
      if (payload.league) params.set('league', String(payload.league));
      if (payload.fromTicker) params.set('src', 'ticker');
      return `${baseUrl}?${params.toString()}`;
    }
  }
  
  return baseUrl;
};

export function NavigationProvider({ children }) {
  const [currentScreen, setCurrentScreen] = useState('Home');
  const [history, setHistory] = useState(['Home']);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  const [routePayload, setRoutePayload] = useState(null);
  
  // Admin and maintenance state
  const [authUser, setAuthUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [alphaAccess, setAlphaAccess] = useState(false);
  const [betaAccess, setBetaAccess] = useState(false);
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  // accessStage: 'alpha' (admin+alpha only), 'beta' (admin+alpha+beta), 'live' (everyone)
  // When set, it overrides comingSoonEnabled logic with a more expressive tier system.
  const [accessStage, setAccessStage] = useState('live');
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerStartedAt, setBannerStartedAt] = useState(null);
  const [communityModalEnabled, setCommunityModalEnabled] = useState(true);
  const [communityRedirectUrl, setCommunityRedirectUrl] = useState('https://discord.gg/your-discord-link');
  const [communityScreenEnabled, setCommunityScreenEnabled] = useState(false);
  const [dfsConsensusExclusionEnabled, setDfsConsensusExclusionEnabled] = useState(false);
  const [photoUploadEnabled, setPhotoUploadEnabled] = useState(false); // off by default; toggled from admin
  
  // Track if user data has been loaded (to prevent race conditions)
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  // True only when userDataLoaded came from a *successful* Firestore read (not an error fallback).
  // Admin guards should only fire when this is true so a transient Firestore error
  // doesn't incorrectly kick admins out.
  const [userDataLoadSuccess, setUserDataLoadSuccess] = useState(false);
  
  // ⭐ Track if we've initialized from URL (should only happen once on mount)
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);

  // Calculate admin status — case-insensitive to guard against capitalisation mismatches.
  // Dev-host bypass: on localhost / 127.0.0.1 (web only) treat the session as admin so
  // admin-gated screens and affordances are accessible without needing a real admin account.
  // Production hostnames and native builds are unaffected.
  const _isDevHost = (() => {
    try {
      if (Platform.OS !== 'web') return false;
      const h = (typeof window !== 'undefined' && window.location?.hostname) || '';
      return h === 'localhost' || h === '127.0.0.1';
    } catch { return false; }
  })();
  const isAdmin = _isDevHost || userType?.toLowerCase() === 'admin';
  
  // Access tier logic based on accessStage:
  //   'alpha' → only admin or alpha testers
  //   'beta'  → admin, alpha, or beta testers
  //   'live'  → everyone (no gate)
  const hasComingSoonAccess = isAdmin || alphaAccess || (accessStage === 'beta' && betaAccess) || accessStage === 'live';
  // Expose the effective gating flag: gate is active when stage is not 'live'
  const stageGateEnabled = comingSoonEnabled || accessStage === 'alpha' || accessStage === 'beta';



  // Get route from location helper
  const getRouteFromLocation = () => {
    if (Platform.OS !== 'web') return '/home';
    try {
      const path = window?.location?.pathname || '/home';
      // hash fallback support (e.g., /#/login)
      const hash = window?.location?.hash || '';
      if (hash.startsWith('#/')) return hash.slice(1);
      return path;
    } catch (e) {
      return '/home';
    }
  };

  // ⭐ Initialize screen from URL on web - runs ONCE on mount only
  useEffect(() => {
    if (Platform.OS !== 'web' || hasInitializedFromUrl) return;
    
    const path = getRouteFromLocation();
    const screen = urlToScreen[path] || 'Home';
    
    // ⭐ Parse URL query params for SgpFlow (e.g., /results?q=penguins%20goals)
    const urlParams = new URLSearchParams(window.location.search);
    const urlQuery = urlParams.get('q') || '';
    const bgQueryId = urlParams.get('bg') || '';
    
    // Guard: SgpFlow requires a query from payload, URL params, or session
    const sgpQueryAvailable = (() => {
      // Check URL params first
      if (urlQuery) {
        // Store the query in session so SgpFlowScreen can pick it up
        try { 
          window.sessionStorage.setItem('sgp:lastQuery', urlQuery);
        } catch {}
        return true;
      }
      // Check background query ID
      if (bgQueryId) {
        try {
          window.sessionStorage.setItem('sgp:bgQueryId', bgQueryId);
        } catch {}
        return true;
      }
      // Fall back to stored session query
      try { 
        const storedQuery = window.sessionStorage.getItem('sgp:lastQuery');
        if (storedQuery) return true;
      } catch { }
      return false;
    })();
    
    // For non-admin-protected pages, initialize immediately
    if (screen !== 'Admin') {
      if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        // If no saved query, send back to Home
        setCurrentScreen('Home');
        setHistory(['Home']);
        setRoutePayload(null);
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'SgpFlow' && urlQuery) {
        setCurrentScreen(screen);
        setHistory([screen]);
        setRoutePayload({
          query: urlQuery,
          fromUrl: true,
          fixtureId: urlParams.get('fixture') || null,
          league: urlParams.get('league') || null,
          fromTicker: urlParams.get('src') === 'ticker',
        });
      } else {
        setCurrentScreen(screen);
        setHistory([screen]);
        setRoutePayload(null);
      }
      setHasInitializedFromUrl(true);
    }
    // For Admin page, wait for user data to load
  }, [hasInitializedFromUrl]);
  
  // ⭐ Separate effect for admin page check - only runs when user data loads
  useEffect(() => {
    if (Platform.OS !== 'web' || !hasInitializedFromUrl) return;
    if (!userDataLoaded) return;
    
    const path = getRouteFromLocation();
    const screen = urlToScreen[path] || 'Home';
    
    // Only handle admin page protection
    if (screen === 'Admin') {
      if (isAdmin) {
        setCurrentScreen('Admin');
        setHistory(['Admin']);
      } else if (userDataLoadSuccess) {
        // Only redirect if we have a *confirmed successful* Firestore read — a transient
        // error falling back to userType:'user' should not kick out a real admin.
        setCurrentScreen('AdminLogin');
        setHistory(['AdminLogin']);
        window.history.replaceState({}, '', '/admin-login');
      }
      // If userDataLoadSuccess is false (error fallback), we wait — don't redirect yet.
    }
  }, [userDataLoaded, userDataLoadSuccess, isAdmin, hasInitializedFromUrl]);
  
  // ⭐ Handle initial admin page on mount (before user data loads)
  useEffect(() => {
    if (Platform.OS !== 'web' || hasInitializedFromUrl) return;
    
    const path = getRouteFromLocation();
    const screen = urlToScreen[path] || 'Home';
    
    // If trying to access admin page, wait for user data to load
    if (screen === 'Admin' && !userDataLoaded) {
      return;
    }
    
    // For admin page, mark as initialized once user data loads
    if (screen === 'Admin' && userDataLoaded) {
      setHasInitializedFromUrl(true);
    }
  }, [hasInitializedFromUrl, userDataLoaded]);

  // Set document title based on current screen
  useEffect(() => {
    if (Platform.OS === 'web') {
      const screenTitles = {
        'Home': 'Home',
        'Login': 'Sign In',
        'Register': 'Register',
        'Explore': 'Explore',
        'SameGameParlay': 'Optimizer',
        'Community': 'Community',
        'SgpFlow': 'Results',
        'Profile': 'Profile',
        'Plans': 'Plans',
        'Success': 'Subscription Success',
        'Admin': 'Admin',
        'AdminLogin': 'Admin Login',
        'ForgotPassword': 'Forgot Password',
        'ResetPassword': 'Reset Password',
        'MainPage': 'Coming Soon',
        'Privacy': 'Privacy Policy',
        'Terms': 'Terms of Service',
      };
      
      const title = screenTitles[currentScreen] || 'Bet Optimized';
      
      // Ensure title is never undefined or null
      if (title && typeof title === 'string' && title.trim() !== '') {
        document.title = title;
      } else {
        document.title = 'Bet Optimized';
      }
    }
  }, [currentScreen]);

  // Set initial title on mount
  useEffect(() => {
    if (Platform.OS === 'web') {
      document.title = 'Bet Optimized';
    }
  }, []);

  // Listen for browser navigation events (back/forward buttons, manual URL changes)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const onPop = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Home';
      // Parse URL params
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = urlParams.get('q') || '';
      
      const sgpQueryAvailable = (() => {
        // Check URL params first
        if (urlQuery) {
          try { 
            window.sessionStorage.setItem('sgp:lastQuery', urlQuery);
          } catch {}
          return true;
        }
        // Fall back to session storage
        try { return !!window.sessionStorage.getItem('sgp:lastQuery'); } catch { return false; }
      })();
      
      // Protect admin page on browser navigation (only check after user data is loaded)
      if (screen === 'Admin' && userDataLoaded && !isAdmin) {
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'Admin' && !userDataLoaded) {
        return;
      } else if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'SgpFlow' && urlQuery) {
        setCurrentScreen(screen);
        setRoutePayload({
          query: urlQuery,
          fromUrl: true,
          fixtureId: urlParams.get('fixture') || null,
          league: urlParams.get('league') || null,
          fromTicker: urlParams.get('src') === 'ticker',
        });
      } else {
        setCurrentScreen(screen);
        setRoutePayload(null);
      }
    };
    
    const onNavigate = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Home';
      
      // Parse URL params
      const urlParams = new URLSearchParams(window.location.search);
      const urlQuery = urlParams.get('q') || '';
      
      const sgpQueryAvailable = (() => {
        if (urlQuery) {
          try { 
            window.sessionStorage.setItem('sgp:lastQuery', urlQuery);
          } catch {}
          return true;
        }
        try { return !!window.sessionStorage.getItem('sgp:lastQuery'); } catch { return false; }
      })();
      
      // Protect admin page on browser navigation (only check after user data is loaded)
      if (screen === 'Admin' && userDataLoaded && userDataLoadSuccess && !isAdmin) {
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else if (screen === 'Admin' && !userDataLoaded) {
        return;
      } else if (screen === 'SgpFlow' && !sgpQueryAvailable) {
        setCurrentScreen('Home');
        window.history.replaceState({}, '', '/home');
      } else {
        setCurrentScreen(screen);
      }
    };
    
    window.addEventListener('popstate', onPop);
    window.addEventListener('app:navigate', onNavigate);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('app:navigate', onNavigate);
    };
  }, [isAdmin, userDataLoaded]);

  // Auth listener (sets auth user). Role is handled by realtime snapshot below
  useEffect(() => {
    // Clean up any existing listener first
    if (typeof window !== 'undefined' && window.__firestoreListeners.authListener) {
      try {
        window.__firestoreListeners.authListener();
        window.__firestoreListeners.authListener = null;
      } catch (error) {
        console.warn('Error cleaning up auth listener:', error);
      }
    }
    
    let unsubscribed = false;
    const unsub = onAuthStateChanged(auth, (u) => {
      if (unsubscribed) return;
      setAuthUser(u || null);
      if (!u) {
        setUserType(null);
      }
    });
    
    // Store unsub function globally
    if (typeof window !== 'undefined') {
      window.__firestoreListeners.authListener = unsub;
    }
    
    return () => {
      unsubscribed = true;
      if (unsub) {
        unsub();
      }
      // Clear global reference
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.authListener = null;
      }
    };
  }, []);

  // Reset user-type state when auth user is cleared (logout).
  // Population of userType/alphaAccess/betaAccess/userDataLoaded is driven by
  // AuthContext via setUserTypeFromAuth — no independent Firestore read needed here.
  useEffect(() => {
    if (!authUser?.uid) {
      setUserType(null);
      setAlphaAccess(false);
      setBetaAccess(false);
      setUserDataLoaded(false);
      setUserDataLoadSuccess(false);
    }
  }, [authUser?.uid]);

  // Called by AuthContext after it loads the user document — avoids a duplicate
  // Firestore read on the same users/{uid} doc that would cause Target ID collisions.
  const setUserTypeFromAuth = useCallback(({ userType: type, alphaAccess: alpha, betaAccess: beta, success }) => {
    setUserType(type || 'user');
    setAlphaAccess(!!alpha);
    setBetaAccess(!!beta);
    setUserDataLoaded(true);
    setUserDataLoadSuccess(!!success);
  }, []);

  // Maintenance listener
  useEffect(() => {
    // Clean up any existing listener first
    if (typeof window !== 'undefined' && window.__firestoreListeners.maintenanceListener) {
      try {
        window.__firestoreListeners.maintenanceListener();
        window.__firestoreListeners.maintenanceListener = null;
      } catch (error) {
        console.warn('Error cleaning up maintenance listener:', error);
      }
    }
    
    let unsubscribed = false;
    let unsub = null;
    let cleanupCalled = false;
    
    
    // Delay startup to let auth-triggered Firestore targets (users/uid) settle first,
    // preventing Target ID collisions between this persistent listener and the user doc.
    const timeoutId = setTimeout(() => {
      if (unsubscribed || cleanupCalled) return;
      
      const ref = doc(db, 'site_maintenance', 'global');
      
      unsub = resilientOnSnapshot(ref, (snap) => {
        if (unsubscribed || cleanupCalled) return;
        
        const d = snap.data() || {};
        setComingSoonEnabled(!!d.comingSoonEnabled);
        setAccessStage(d.accessStage || 'live');
        setBannerEnabled(!!d.maintenanceBannerEnabled);
        setBannerStartedAt(d.maintenanceBannerStartedAt || null);
        setCommunityModalEnabled(d.communityModalEnabled !== false);
        setCommunityRedirectUrl(d.communityRedirectUrl || 'https://discord.gg/your-discord-link');
        setPhotoUploadEnabled(!!d.photoUploadEnabled);
      }, (error) => {
        if (unsubscribed || cleanupCalled) return;
        console.warn('[NavigationContext] Maintenance listener error (non-fatal):', error.code || error.message);
      });
      
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.maintenanceListener = unsub;
      }
    }, 800);
    
    return () => {
      unsubscribed = true;
      cleanupCalled = true;
      clearTimeout(timeoutId);
      if (unsub) {
        try {
          unsub();
        } catch (error) {
          console.error('❌ [NavigationContext] Error unsubscribing from maintenance:', error);
        }
      }
      // Clear global reference
      if (typeof window !== 'undefined') {
        window.__firestoreListeners.maintenanceListener = null;
      }
    };
  }, []);

  // Prod host detection
  const isProdHost = useMemo(() => {
    if (Platform.OS !== 'web') return false;
    try {
      const host = window.location.hostname;
      return host && host !== 'localhost' && host !== '127.0.0.1';
    } catch {
      return false;
    }
  }, []);

  // Listen to site_config/feature_flags for feature toggles (communityScreenEnabled, etc.)
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'site_config', 'feature_flags'),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setCommunityScreenEnabled(d.communityScreenEnabled === true);
        setDfsConsensusExclusionEnabled(d.dfsConsensusExclusionEnabled !== false);
      },
      () => {}
    );
    return () => unsub();
  }, []);

  // Redirect users without access to coming-soon in prod when coming soon is enabled
  useEffect(() => {
    if (!isProdHost) return;
    // ⭐ Wait for initial URL parsing to complete before running stage gate
    if (!hasInitializedFromUrl) return;
    // ⭐ Wait for user data to load before checking access - prevents race condition after login
    if (!userDataLoaded && authUser) return; // If user is logged in but data not loaded, wait
    
    if (stageGateEnabled && !hasComingSoonAccess && currentScreen !== 'MainPage' && currentScreen !== 'Admin' && currentScreen !== 'Login' && currentScreen !== 'Register' && currentScreen !== 'ForgotPassword' && currentScreen !== 'ResetPassword') {
      console.log('[NavigationContext] Redirecting to Coming Soon - stageGateEnabled:', stageGateEnabled, 'accessStage:', accessStage, 'hasComingSoonAccess:', hasComingSoonAccess, 'userDataLoaded:', userDataLoaded);
      try {
        window.history.pushState({}, '', '/coming-soon');
        window.dispatchEvent(new Event('app:navigate'));
      } catch {
        setCurrentScreen('MainPage');
      }
    }
  }, [stageGateEnabled, comingSoonEnabled, accessStage, hasComingSoonAccess, currentScreen, isProdHost, userDataLoaded, authUser, hasInitializedFromUrl]);

  const navigateTo = (screenName, options = {}) => {
    console.log('[Nav] 📍 navigateTo:', screenName, 'from:', currentScreen, 'options:', options);
    
    // Special case: allow re-searching from the results page by updating routePayload
    // without a full screen transition. This fires SgpFlowScreen's routePayload useEffect.
    if (currentScreen === 'SgpFlow' && screenName === 'SgpFlow' && options?.payload?.query) {
      const newQuery = String(options.payload.query);
      try { window.sessionStorage.setItem('sgp:lastQuery', newQuery); } catch {}
      setRoutePayload(options.payload);
      const url = buildUrlWithParams('SgpFlow', options.payload, 'SgpFlow');
      window.history.pushState({}, '', url);
      return;
    }

    // Don't navigate if already on the same screen (unless forced or has different payload)
    if (currentScreen === screenName && !options?.force) {
      console.log('[Nav] ℹ️ Already on screen:', screenName);
      return;
    }

    // Handle redirect after login
    if (screenName === 'Login' && options.redirectAfterLogin) {
      setRedirectAfterLogin(options.redirectAfterLogin);
    }

    // Protect admin page - only block if we have a *confirmed* successful Firestore read
    if (screenName === 'Admin' && userDataLoaded && userDataLoadSuccess && !isAdmin) {
      console.log('[Nav] ⚠️ Access denied: Admin page requires admin privileges');
      return;
    }
    
    // Wait for user data to load before allowing admin navigation
    if (screenName === 'Admin' && !userDataLoaded) {
      console.log('[Nav] ⏳ Waiting for user data to load before admin navigation...');
      return;
    }

    // Guard: SgpFlow requires a query from payload or session
    if (screenName === 'SgpFlow') {
      const hasPayloadQuery = !!options?.payload?.query;
      let hasStored = false;
      try { hasStored = !!window.sessionStorage.getItem('sgp:lastQuery'); } catch {}
      if (!hasPayloadQuery && !hasStored) {
        console.log('[Nav] ⚠️ Blocked navigation: SgpFlow requires a query');
        return;
      }
      // Save payload query if present
      if (hasPayloadQuery) {
        try { 
          window.sessionStorage.setItem('sgp:lastQuery', String(options.payload.query)); 
          console.log('[Nav] ✅ Stored query in session:', options.payload.query);
        } catch {}
      }
    }

    // Check if current screen has an exit animation
    if (Platform.OS === 'web' && currentScreen === 'Explore' && window.exploreExitAnimation) {
      // Run exit animation before navigating
      window.exploreExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'SameGameParlay' && window.sameGameParlayExitAnimation) {
      // Run exit animation before navigating
      window.sameGameParlayExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Home' && window.homeExitAnimation) {
      // Run exit animation before navigating
      window.homeExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'SgpFlow' && window.sgpFlowExitAnimation) {
      // Run exit animation before navigating
      window.sgpFlowExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Profile' && window.profileExitAnimation) {
      // Run exit animation before navigating
      window.profileExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
      });
    } else if (Platform.OS === 'web' && currentScreen === 'Plans' && window.plansExitAnimation) {
      // Run exit animation before navigating
      window.plansExitAnimation(() => {
        setCurrentScreen(screenName);
        setHistory(prev => [...prev, screenName]);
        setRoutePayload(options?.payload || null);

        // Update URL on web with query params
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        window.history.pushState({}, '', url);
      });
    } else {
      // Normal navigation without exit animation
      console.log('[Nav] ✅ Navigating to:', screenName);
      setCurrentScreen(screenName);
      setRoutePayload(options?.payload || null);
      setHistory(prev => [...prev, screenName]);

      // Update URL on web with query params
      if (Platform.OS === 'web') {
        const url = buildUrlWithParams(screenName, options?.payload, currentScreen);
        console.log('[Nav] 📝 Updating URL to:', url);
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      }
    }
  };

  const goBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop(); // Remove current screen
      const previousScreen = newHistory[newHistory.length - 1];
      
      setCurrentScreen(previousScreen);
      setHistory(newHistory);

      // Update URL on web
      if (Platform.OS === 'web') {
        const url = screenToUrl[previousScreen] || '/';
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('app:navigate'));
      }
    }
  };

  const replaceScreen = (screenName) => {
    setCurrentScreen(screenName);
    setHistory(prev => [...prev.slice(0, -1), screenName]);

    // Update URL on web
    if (Platform.OS === 'web') {
      const url = screenToUrl[screenName] || '/';
      window.history.replaceState({}, '', url);
      window.dispatchEvent(new Event('app:navigate'));
    }
  };

  const handleLoginRedirect = () => {
    if (redirectAfterLogin) {
      const redirectScreen = redirectAfterLogin;
      setRedirectAfterLogin(null);
      navigateTo(redirectScreen);
    } else {
      navigateTo('Home');
    }
  };

  // Banner component
  const Banner = () => {
    if (!bannerEnabled) return null;
    
    return (
      <View style={bannerStyles.banner}>
        <Text style={bannerStyles.bannerText}>
          Site maintenance in progress{bannerStartedAt && bannerStartedAt.toDate ? ` — started ${bannerStartedAt.toDate().toLocaleString()}` : ''}
        </Text>
      </View>
    );
  };

  return (
    <NavigationContext.Provider value={{
      currentScreen,
      navigateTo,
      goBack,
      replaceScreen,
      history,
      handleLoginRedirect,
      // Direct payload setter for triggering queries without navigation
      setRoutePayloadDirect: setRoutePayload,
      // Admin and maintenance state
      authUser,
      userType,
      isAdmin,
      alphaAccess,
      betaAccess,
      setUserTypeFromAuth,
      accessStage,
      hasComingSoonAccess,
      stageGateEnabled,
      comingSoonEnabled,
      bannerEnabled,
      bannerStartedAt,
      communityModalEnabled,
      communityRedirectUrl,
      communityScreenEnabled,
      dfsConsensusExclusionEnabled,
      photoUploadEnabled,
      Banner,
      routePayload
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

const bannerStyles = {
  banner: { 
    backgroundColor: '#1f2937', 
    paddingVertical: 6, 
    paddingHorizontal: 12, 
    borderBottomWidth: 1, 
    borderBottomColor: 'rgba(255,255,255,0.08)' 
  },
  bannerText: { 
    color: 'rgba(255,255,255,0.9)', 
    textAlign: 'center' 
  }
};
